import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { z } from 'zod';

// Zod schemas for validation
export const TrackSettingsSchema = z.object({
  visible: z.boolean(),
  height: z.number().min(10).max(200),
  order: z.number().optional(),
});

export const TimelineTrackSettingsSchema = z.record(z.string(), TrackSettingsSchema);

export type TrackSettings = z.infer<typeof TrackSettingsSchema>;

interface TimelineTrackSettingsState {
  trackSettings: Record<string, TrackSettings>;

  // Actions
  setTrackVisible: (trackKey: string, visible: boolean) => void;
  setTrackHeight: (trackKey: string, height: number) => void;
  setTrackOrder: (trackKey: string, order: number) => void;
  getTrackSettings: (trackKey: string) => TrackSettings;
  resetTrackSettings: () => void;
  initializeTrack: (trackKey: string, defaultHeight: number) => void;
  _validateSettings: (settings: unknown) => Record<string, TrackSettings>;
}

const DEFAULT_TRACK_SETTINGS: TrackSettings = {
  visible: true,
  height: 50,
};

export const useTimelineTrackSettingsStore = create<TimelineTrackSettingsState>()(
  persist(
    (set, get) => ({
      trackSettings: {},

      // Validate and parse settings from storage
      _validateSettings: (settings: unknown): Record<string, TrackSettings> => {
        try {
          return TimelineTrackSettingsSchema.parse(settings);
        } catch (error) {
          console.warn('[TimelineTrackSettings] Invalid settings in storage, resetting to defaults', error);
          return {};
        }
      },

      setTrackVisible: (trackKey: string, visible: boolean) => {
        set((state) => ({
          trackSettings: {
            ...state.trackSettings,
            [trackKey]: {
              ...state.trackSettings[trackKey],
              visible,
            },
          },
        }));
      },

      setTrackHeight: (trackKey: string, height: number) => {
        set((state) => ({
          trackSettings: {
            ...state.trackSettings,
            [trackKey]: {
              ...state.trackSettings[trackKey],
              height: Math.max(10, Math.min(200, height)), // Clamp between 10-200
            },
          },
        }));
      },

      setTrackOrder: (trackKey: string, order: number) => {
        set((state) => ({
          trackSettings: {
            ...state.trackSettings,
            [trackKey]: {
              ...state.trackSettings[trackKey],
              order,
            },
          },
        }));
      },

      getTrackSettings: (trackKey: string): TrackSettings => {
        const settings = get().trackSettings[trackKey];
        return settings || DEFAULT_TRACK_SETTINGS;
      },

      initializeTrack: (trackKey: string, defaultHeight: number) => {
        const existing = get().trackSettings[trackKey];
        if (!existing) {
          set((state) => ({
            trackSettings: {
              ...state.trackSettings,
              [trackKey]: {
                visible: true,
                height: defaultHeight,
              },
            },
          }));
        }
      },

      resetTrackSettings: () => {
        set({ trackSettings: {} });
      },
    }),
    {
      name: 'timeline-track-settings',
      // Validate data when loading from storage
      onRehydrateStorage: () => (state) => {
        if (state?.trackSettings) {
          try {
            state.trackSettings = TimelineTrackSettingsSchema.parse(state.trackSettings);
          } catch (error) {
            console.warn('[TimelineTrackSettings] Failed to validate rehydrated state, resetting', error);
            state.trackSettings = {};
          }
        }
      },
    }
  )
);
